/**
 * « Ta fiche de ce soir est dans ton carnet » — la carte discrète du carnet papier.
 *
 * Le carnet de la semaine est préparé le dimanche et imprimé par un parent :
 * l'enfant travaille **sans écran**. Cette carte ne montre donc ni énoncé ni
 * corrigé — la route n'en porte pas (règle 2) — seulement le code de la fiche,
 * pour que l'enfant prenne la bonne page, et le bouton qui envoie la photo.
 *
 * **Une photo pour la fiche entière** : elle est posée sur chaque exercice de la
 * fiche, que le parent juge ensuite un à un sur l'écran « À valider ». La photo
 * part au stockage par URL présignée, et seule sa clé traverse l'API
 * (invariant 7), exactement comme pour une feuille d'entraînement.
 *
 * La durée de la fiche est annoncée **en mots** (« environ vingt-cinq
 * minutes »), jamais en chiffres — §14.1 au collège —, et chaque forme sur
 * cahier (dictée, opération posée, construction, écriture courte) ajoute sa
 * phrase « sur ton cahier ». Le texte d'une dictée n'est jamais ici : la route
 * de l'enfant ne le porte pas.
 *
 * Le ton suit l'âge (`voit_ses_chiffres`, le même repère que « Ma semaine ») :
 * au collège une phrase qui accompagne, au lycée une ligne factuelle.
 *
 * **Rien quand il n'y a rien** : pas de fiche ce soir, carnet éteint (la route
 * répond 404), ou panne — la carte n'occupe pas l'écran du soir pour dire
 * qu'elle n'a rien à dire. Aucun lien vers un PDF : les PDF sont des routes du
 * parent, et un test balaie `src/child/**` pour l'affirmer.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import type { MaFicheOut } from "@/api/schema";
import { uploadPhoto } from "@/photo/upload";
import { Card, ErrorNote } from "@/ui/primitives";

/** Les phrases de la carte, selon l'âge. Pures, et exportées pour être testées. */
export function phrasesDeLaFiche(fiche: MaFicheOut): {
  titre: string;
  consigne: string;
  rendue: string;
} {
  const exercices = `${fiche.exercices} exercice${fiche.exercices > 1 ? "s" : ""}`;
  if (fiche.voit_ses_chiffres) {
    return {
      titre: `Carnet — fiche ${fiche.code}`,
      consigne: `${fiche.titre} : ${exercices} sur papier. Une photo de la fiche une fois faite.`,
      rendue: `Fiche ${fiche.code} rendue.`,
    };
  }
  return {
    titre: "Ta fiche de ce soir est dans ton carnet",
    consigne: `Prends la fiche ${fiche.code} (${exercices}). Quand tu as fini, photographie-la.`,
    rendue: "C'est envoyé. Ton parent va regarder ta fiche avec la correction.",
  };
}

type Envoi = "repos" | "envoi" | "panne";

export function FicheDuCarnet() {
  const [fiche, setFiche] = useState<MaFicheOut | null>(null);
  const [envoi, setEnvoi] = useState<Envoi>("repos");

  const charger = useCallback(async () => {
    try {
      const reponse = await call("maFicheDuSoir");
      setFiche(reponse.fiche ?? null);
    } catch {
      // 404 quand le carnet est éteint, ou une panne : dans les deux cas, rien.
      setFiche(null);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (!fiche) return null;
  const phrases = phrasesDeLaFiche(fiche);
  const surPapier = fiche.sur_papier ?? [];

  const photographier = async (file: File) => {
    setEnvoi("envoi");
    try {
      const photo = await uploadPhoto(file);
      const rendue = await call("photographierMaFiche", {
        params: { fiche_id: fiche.id },
        body: { key: photo.key },
      });
      setFiche(rendue);
      setEnvoi("repos");
    } catch {
      setEnvoi("panne");
    }
  };

  return (
    <Card title={phrases.titre}>
      {fiche.rendue ? (
        <p className="muted small">{phrases.rendue}</p>
      ) : (
        <>
          <p className="muted small">{phrases.consigne}</p>
          {fiche.duree_annoncee ? (
            <p className="muted small">Prévois {fiche.duree_annoncee}.</p>
          ) : null}
          {surPapier.map((phrase) => (
            <p key={phrase} className="small">
              {phrase}
            </p>
          ))}
          <label className="devoirs__prise">
            <strong>Photographier ma fiche</strong>
            <input
              className="input"
              type="file"
              accept="image/*"
              capture="environment"
              aria-label="Photographier ma fiche"
              disabled={envoi === "envoi"}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void photographier(file);
              }}
            />
          </label>
        </>
      )}
      {envoi === "envoi" ? <p className="muted small">Envoi…</p> : null}
      {envoi === "panne" ? (
        <ErrorNote>La photo n'est pas partie. Réessaie.</ErrorNote>
      ) : null}
    </Card>
  );
}
